/**
 * DriftManagementTab Component
 *
 * The drift queue for DSC tenants (`baselineSource === 'DSC'`): drift
 * detection, approval workflows, batch processing and audit export, on
 * Carbon (mockup 02). An action row (the last assessment and "Apply staged
 * changes"), the queue notices, then the Drifts DataTable with Open / Back
 * to baseline status tabs, most severe first, rows that expand in place into
 * the drift detail (D25), and the modals. The Open tab always leaves out
 * Remediated; hiding Awaiting remediation is a group in the toolbar's filter
 * menu.
 *
 * Part of the Baseline Compliance hub.
 */

import { useState, useEffect, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ConfigurationDrift } from '@/types'
import DriftTableWithBulkActions from '@/components/Drift/DriftTableWithBulkActions'
import DriftDetailDrawer, { DriftDetailContent } from '@/components/Drift/DriftDetailDrawer'
import RejectionNotificationModal from '@/components/RejectionNotificationModal'
import DriftAuditExportModal from '@/components/Drift/DriftAuditExportModal'
import toast from 'react-hot-toast'
import { useTenantContext } from '@/hooks/useTenantContext'
import LastAssessed from '@/components/common/LastAssessed'
import { Button } from '@/components/carbon/Button'
import { ContainedTabs } from '@/components/carbon/ContainedTabs'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { canChangeDrifts } from '@/lib/drift-permissions'

// Honest in-flight state: we know the total going in, but we don't know
// sub-step progress because processBatchedApprovals is a single Promise
// that resolves at the end. No fake percentage, no fake stages.
type ProcessingState = { isProcessing: boolean; totalToProcess: number }
const IDLE_PROCESSING_STATE: ProcessingState = { isProcessing: false, totalToProcess: 0 }

export interface DriftManagementTabProps {
  /** The tenant ID to display drift data for (parent handles tenant selection) */
  tenantId: string | undefined
  /**
   * Optional controlled-mode selection for the drift detail drawer. When
   * provided, the parent owns the drawer's open state — useful when other
   * surfaces on the page (e.g. the "Top items" rows) need to open the same
   * drawer without rendering a parallel instance. When omitted the component
   * falls back to its own internal state so existing callers keep working.
   */
  selectedDriftId?: string | null
  // Wave 7 codex fix — widened to accept the optional source hint that
  // HybridDriftManagementTab uses. DSC tab is single-source so the hint is
  // a no-op here; signature widened to keep the parent's `handleDriftSelect`
  // callback assignable to both tabs without an adapter. Existing callers
  // passing `(id)` still type-check because the second parameter is optional.
  onSelectDrift?: (id: string | null, _sourceHint?: string) => void
  /** Nothing scanned or the stats call failed: the status tabs show no counts rather than zeros. */
  countsUnknown?: boolean
  /** Nothing scanned: the empty Open view says there is no baseline data yet. */
  unscanned?: boolean
}

export default function DriftManagementTab({
  tenantId,
  selectedDriftId: controlledSelectedDriftId,
  onSelectDrift,
  countsUnknown = false,
  unscanned = false,
}: DriftManagementTabProps) {
  const { userTenants, isGlobalAdmin } = useTenantContext()
  const [filters, setFilters] = useState<{
    tenantId: string
    severity: string
    status: string
    fromDate?: string
    toDate?: string
  }>({
    tenantId: '',
    severity: '',
    status: '',
    fromDate: '',
    toDate: '',
  })
  // The Open tab always leaves out Remediated (Back to baseline holds those
  // rows); hiding Awaiting remediation is the viewer's choice.
  const [hideAwaitingRemediation, setHideAwaitingRemediation] = useState(false)
  const [viewMode, setViewMode] = useState<'active' | 'archive'>('active')
  const [currentPage, setCurrentPage] = useState(1)
  const [internalSelectedDriftId, setInternalSelectedDriftId] = useState<string | null>(null)
  // Controlled vs uncontrolled selection: prefer the parent's state when it's
  // managing the drawer, otherwise fall back to local state.
  const isSelectionControlled = controlledSelectedDriftId !== undefined
  const selectedDriftId = isSelectionControlled
    ? controlledSelectedDriftId ?? null
    : internalSelectedDriftId
  const setSelectedDriftId = (id: string | null) => {
    if (isSelectionControlled) {
      onSelectDrift?.(id)
    } else {
      setInternalSelectedDriftId(id)
    }
  }
  const [showRejectionNotification, setShowRejectionNotification] = useState(false)
  const [rejectedDriftCount, setRejectedDriftCount] = useState(0)
  const [showAuditExportModal, setShowAuditExportModal] = useState(false)
  const pageSize = 20

  const queryClient = useQueryClient()

  // Viewers can read drifts but not act on them (the server 403s every drift
  // write below Approver), so Apply, the queue notice, bulk actions and the
  // detail's Accept / Reject are hidden for them. Shares the ['currentUser']
  // cache with the page and useTenantContext.
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: api.getCurrentUser,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
  const canWrite = canChangeDrifts(currentUser)

  // Sync filters.tenantId from the tenantId prop
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      tenantId: tenantId || '',
    }))
    setCurrentPage(1)
  }, [tenantId])

  const {
    data: driftData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['configuration-drift', filters, currentPage, hideAwaitingRemediation, viewMode],
    queryFn: () => {
      let statusFilter = filters.status

      if (viewMode === 'archive') {
        statusFilter = 'Remediated'
      } else {
        const allStatuses = ['New', 'Pending', 'PendingApproval', 'Approved', 'Rejected', 'Ignored', 'AwaitingRemediation', 'Remediated']
        const excludedStatuses: string[] = ['Remediated']

        if (hideAwaitingRemediation) excludedStatuses.push('AwaitingRemediation')

        if (!statusFilter) {
          const includedStatuses = allStatuses.filter(s => !excludedStatuses.includes(s))
          statusFilter = includedStatuses.join(',')
        } else {
          const currentStatusList = statusFilter.split(',')
          const filteredStatusList = currentStatusList.filter(s => !excludedStatuses.includes(s))
          statusFilter = filteredStatusList.length > 0 ? filteredStatusList.join(',') : 'New'
        }
      }

      const effectiveTenantId = !isGlobalAdmin && userTenants.length === 1
        ? userTenants[0].tenantId
        : filters.tenantId

      if (isGlobalAdmin && (!effectiveTenantId || effectiveTenantId === '')) {
        // Match api.getConfigurationDrifts' return shape so the useQuery
        // overload picks up the right TQueryFnData and downstream `driftData`
        // accessors (.items / .total) keep typing.
        return Promise.resolve({
          items: [] as ConfigurationDrift[],
          total: 0,
          page: currentPage,
          limit: pageSize,
        })
      }

      return api.getConfigurationDrifts({
        tenantId: effectiveTenantId || undefined,
        severity: filters.severity || undefined,
        status: statusFilter || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        page: currentPage,
        limit: pageSize,
        // Most severe first, then most recent (mockup 02), so the first page
        // holds the drifts worth closing first.
        sortBy: 'severity',
      })
    },
    placeholderData: keepPreviousData,
    enabled: !!tenantId,
  })

  const approveMutation = useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      api.approveDrift(id, comment),
    onSuccess: (response, { id }) => {
      const updatedDrift = response.data
      const autoApprovedCount = response.autoApprovedCount || 0
      const message = response.message || "Drift accepted."

      queryClient.setQueryData(['configuration-drift', id], updatedDrift)

      queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
      queryClient.invalidateQueries({ queryKey: ['queued-approvals'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.refetchQueries({ queryKey: ['configuration-drift'] })

      toast.success(
        <div>
          <div className="font-semibold">{message}</div>
          <div className="mt-1 text-helper text-text-secondary">
            {autoApprovedCount > 0
              ? `Changes are staged for the next batch. Click "Apply staged changes" to create pull requests.`
              : `The change is staged for the next batch. Click "Apply staged changes" to create pull requests.`
            }
          </div>
        </div>,
        { duration: autoApprovedCount > 0 ? 8000 : 6000 }
      )
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to accept drift')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      api.rejectDrift(id, comment),
    onSuccess: (updatedDrift, { id }) => {
      queryClient.setQueryData(['configuration-drift', id], updatedDrift)

      queryClient.setQueryData(['configuration-drift', filters, currentPage], (oldData: any) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          items: oldData.items.map((item: any) =>
            item.id === id ? updatedDrift : item
          )
        }
      })

      queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.refetchQueries({ queryKey: ['configuration-drift'] })
      toast.success('Drift rejected.')

      setRejectedDriftCount(1)
      setShowRejectionNotification(true)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reject drift')
    },
  })

  // Query for pending batched approvals count
  const { data: queuedApprovals } = useQuery({
    queryKey: ['queued-approvals', filters.tenantId],
    queryFn: () => {
      return api.getConfigurationDrifts({
        tenantId: filters.tenantId,
        status: 'Approved',
        unprocessedOnly: true,
        limit: 1000
      })
    },
    enabled: !!filters.tenantId && filters.tenantId !== '',
    refetchInterval: 30000,
    select: (data) => ({
      count: data?.items?.length || 0,
      oldestApproval: data?.items?.reduce((oldest: any, current: any) => {
        if (!oldest) return current
        return new Date(current.approvedAt) < new Date(oldest.approvedAt) ? current : oldest
      }, null)
    })
  })

  // Query for recent scan jobs to show last drift check time
  const { data: recentScanJobs } = useQuery({
    queryKey: ['recent-scan-jobs', filters.tenantId],
    queryFn: () => api.getRecentScanJobs(filters.tenantId, 5),
    enabled: !!filters.tenantId && filters.tenantId !== '',
    refetchInterval: 60000,
  })

  // Cheap "Back to baseline" count for the status tab. We only ever need
  // `total` here, so request a single-row page — payload size stays small
  // while the response still carries the unfiltered Remediated total.
  const { data: archiveCountData } = useQuery({
    queryKey: ['configuration-drift-archive-count', filters.tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId: filters.tenantId,
        status: 'Remediated',
        page: 1,
        limit: 1,
      }),
    enabled: !!filters.tenantId && filters.tenantId !== '',
    staleTime: 5 * 60 * 1000,
  })
  const archiveCount = archiveCountData?.total

  // Dashboard stats — feed the Severity select's counts in the table toolbar.
  // Same key as the page and the Command Center, so the cache is shared.
  const { data: statsData } = useQuery({
    queryKey: ['dashboard', filters.tenantId],
    queryFn: () => api.getDashboardStats(filters.tenantId),
    enabled: !!filters.tenantId && filters.tenantId !== '',
    refetchInterval: 5 * 60 * 1000,
  })

  // Find the last completed ConfigurationScan job
  const lastCompletedScan = recentScanJobs?.find(
    (job: any) => job.jobType === 'ConfigurationScan' &&
      (job.status === 'Completed' || job.status === 'CompletedWithDrift')
  )

  const [processingState, setProcessingState] = useState<ProcessingState>(IDLE_PROCESSING_STATE)

  const processBatchMutation = useMutation({
    mutationFn: async (tid: string) => {
      const totalToProcess = queuedApprovals?.count || 0
      setProcessingState({
        isProcessing: true,
        totalToProcess
      })

      toast.loading(
        <div>
          <div className="font-semibold">
            Applying {totalToProcess} accepted change{totalToProcess === 1 ? '' : 's'}
          </div>
          <div className="mt-1 text-helper text-text-secondary">This may take a few minutes.</div>
        </div>,
        { id: 'batch-processing', duration: Infinity }
      )

      try {
        const result = await api.processBatchedApprovals(tid)
        return result
      } catch (error) {
        setProcessingState(IDLE_PROCESSING_STATE)
        throw error
      }
    },
    onSuccess: (result) => {
      toast.dismiss('batch-processing')

      setProcessingState(IDLE_PROCESSING_STATE)

      try {
        queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
        queryClient.invalidateQueries({ queryKey: ['queued-approvals'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.refetchQueries({ queryKey: ['configuration-drift'] })
      } catch (error) {
        console.error('Error refreshing queries after batch processing:', error)
      }

      if (result?.pullRequests && Array.isArray(result.pullRequests) && result.pullRequests.length > 0) {
        toast.success(
          <div>
            <div className="font-semibold">{result.pullRequests.length} pull request{result.pullRequests.length === 1 ? '' : 's'} created. Baseline changes apply when they merge.</div>
            <div className="mt-2 text-helper">
              <div className="mb-1 font-semibold">{result.processed} change{result.processed === 1 ? '' : 's'} staged:</div>
              <div className="flex flex-col gap-1">
                {result.pullRequests.map(pr => (
                  <div key={pr.prId} className="flex items-center justify-between gap-3 bg-layer-1 px-2 py-1">
                    <span className="text-text-secondary">{pr.driftCount} changes for {pr.tenantId.substring(0, 8)}</span>
                    {isGlobalAdmin ? (
                      <a
                        href={pr.prUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-link hover:text-link-hover hover:underline"
                      >
                        View PR #{pr.prId}
                      </a>
                    ) : (
                      <span className="font-semibold text-text-secondary">
                        PR #{pr.prId} (pending)
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-2 text-helper text-text-secondary">
              {isGlobalAdmin
                ? "Changes will be reflected in the baseline after PR approval and merge."
                : "The iuvo team will review and merge these baseline updates. Changes will be reflected in subsequent scans."
              }
            </div>
          </div>,
          { duration: 12000 }
        )
      } else if (result?.processed > 0) {
        toast.success(
          <div>
            <div className="font-semibold">{result.processed} change{result.processed === 1 ? '' : 's'} applied.</div>
            <div className="mt-1 text-helper text-text-secondary">Baseline updates have been applied directly (no PRs created).</div>
          </div>,
          { duration: 6000 }
        )
      } else {
        toast('No changes were processed. All accepted drifts may have already been handled.', { duration: 4000 })
      }
    },
    onError: (error: any) => {
      toast.dismiss('batch-processing')

      setProcessingState(IDLE_PROCESSING_STATE)

      console.error('Batch processing error:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred'
      toast.error(
        <div>
          <div className="font-semibold">Failed to apply staged changes</div>
          <div className="mt-1 text-helper">{errorMessage}</div>
          <div className="mt-2 text-helper text-text-secondary">
            Your accepted changes are still saved. You can try applying them again.
          </div>
        </div>,
        { duration: 8000 }
      )
    },
  })

  const handleApprove = (id: string, comment?: string) => {
    approveMutation.mutate({ id, comment })
  }

  const handleReject = (id: string, comment?: string) => {
    rejectMutation.mutate({ id, comment })
  }

  const handleProcessQueuedChanges = () => {
    if (!filters.tenantId) return
    processBatchMutation.mutate(filters.tenantId)
  }

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters({
      ...newFilters,
      tenantId: tenantId || '',
    })
    setCurrentPage(1)
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        title="Couldn't load the drift queue."
        actions={
          <Button kind="tertiary" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        Your monitoring is unaffected. Try again in a moment.
      </InlineNotification>
    )
  }

  const hasTenant = !!filters.tenantId && filters.tenantId !== ''
  const activeJobFromServer = recentScanJobs?.find(
    (job: any) =>
      (job.status === 'Queued' || job.status === 'Running') &&
      (job.jobType === 'ConfigurationScan' || job.jobType === 'BaselineRefresh'),
  )
  const isScanning = !!activeJobFromServer
  const queuedCount = queuedApprovals?.count ?? 0
  const oldestApprovalDate = queuedApprovals?.oldestApproval?.approvedAt
    ? new Date(queuedApprovals.oldestApproval.approvedAt)
    : null
  const activeBadge = viewMode === 'active' ? driftData?.total : undefined
  // In archive mode `driftData.total` is the archive total — fall back to the
  // independent archive-count query when we're in active mode so the tab
  // still reads correctly without a second roundtrip per view-flip.
  const archiveBadge = viewMode === 'archive' ? driftData?.total : archiveCount
  const busyApplying = processBatchMutation.isPending

  const applyButton = (label: ReactNode) => (
    <Button kind="primary" size="md" onClick={handleProcessQueuedChanges} disabled={busyApplying}>
      {label}
      <span className="ml-2 tabular-nums">{queuedCount}</span>
    </Button>
  )

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Action row: when Guardian last assessed the tenant, and Apply. The
          actor is always Guardian (D36), never the scan job's createdBy
          ("System" or an email), and no cadence: a tenant's scan schedule
          may be Manual or Weekly. */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <LastAssessed
          at={lastCompletedScan ? lastCompletedScan.completedAt || lastCompletedScan.createdAt : null}
          status={isScanning ? 'running' : 'ok'}
        />
        {queuedCount > 0 && canWrite
          ? applyButton(processingState.isProcessing ? `Working on ${processingState.totalToProcess}` : 'Apply staged changes')
          : null}
      </div>

      {/* Queue notices: a batch in flight, or changes waiting to apply. (The
          Back to baseline view needs none: the table's description says it.) */}
      {viewMode === 'archive' ? null : processingState.isProcessing ? (
        <InlineNotification
          kind="info"
          title={`${processingState.totalToProcess} change${processingState.totalToProcess === 1 ? ' is' : 's are'} landing now.`}
        >
          Guardian shows the pull requests as soon as the batch returns. This usually takes a few minutes.
        </InlineNotification>
      ) : queuedCount > 0 && canWrite ? (
        <InlineNotification
          kind="info"
          title={`${queuedCount} accepted drift${queuedCount === 1 ? ' is' : 's are'} waiting to apply.`}
          actions={applyButton('Apply now')}
        >
          {oldestApprovalDate
            ? `The oldest has waited since ${oldestApprovalDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}. `
            : ''}
          Apply staged changes opens the pull requests.
        </InlineNotification>
      ) : null}

      {/* The queue: status tabs, the toolbar (whose filter menu also holds what
          the Open tab hides), batch bar, rows that expand in place. */}
      <DriftTableWithBulkActions
        drifts={driftData?.items || []}
        isLoading={isLoading}
        onRowClick={setSelectedDriftId}
        selectedId={selectedDriftId}
        onRefresh={() => {
          queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
          queryClient.invalidateQueries({ queryKey: ['queued-approvals'] })
          queryClient.refetchQueries({ queryKey: ['configuration-drift'] })
          queryClient.refetchQueries({ queryKey: ['queued-approvals'] })
        }}
        pagination={{
          current: currentPage,
          total: driftData?.total || 0,
          pageSize,
          onPageChange: setCurrentPage,
        }}
        filters={filters}
        onFilterChange={handleFilterChange}
        severityCounts={
          statsData?.issuesBySeverity
            ? {
                critical: statsData.issuesBySeverity.critical ?? 0,
                high: statsData.issuesBySeverity.high ?? 0,
                medium: statsData.issuesBySeverity.medium ?? 0,
                low: statsData.issuesBySeverity.low ?? 0,
              }
            : undefined
        }
        countsUnknown={countsUnknown}
        unscanned={unscanned}
        archiveMode={viewMode === 'archive'}
        readOnly={!canWrite}
        tabs={
          <ContainedTabs
            aria-label="Drift status"
            value={viewMode}
            onChange={(id) => {
              setViewMode(id as 'active' | 'archive')
              setCurrentPage(1)
            }}
            tabs={[
              { id: 'active', label: 'Open', count: countsUnknown ? null : (activeBadge ?? null), disabled: !hasTenant },
              { id: 'archive', label: 'Back to baseline', count: countsUnknown ? null : (archiveBadge ?? null), disabled: !hasTenant },
            ]}
          />
        }
        // Before the first assessment there is no audit history to export.
        onExport={hasTenant && !(countsUnknown && !driftData?.items.length) ? () => setShowAuditExportModal(true) : undefined}
        hiddenStatuses={
          viewMode === 'active'
            ? {
                awaiting: hideAwaitingRemediation,
                onChange: ({ awaiting }) => {
                  setHideAwaitingRemediation(awaiting)
                  setCurrentPage(1)
                },
              }
            : undefined
        }
        renderDetail={(drift) => (
          <DriftDetailContent
            driftId={drift.id}
            source="DSC"
            onApprove={handleApprove}
            onReject={handleReject}
            isUpdating={approveMutation.isPending || rejectMutation.isPending}
            readOnly={!canWrite}
            layout="row"
          />
        )}
      />

      {/* Drift detail drawer: for a drift opened from outside the table (a
          controlled selectedDriftId); the table's own rows expand in place. */}
      <DriftDetailDrawer
        driftId={selectedDriftId}
        isOpen={!!selectedDriftId}
        onClose={() => setSelectedDriftId(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        isUpdating={
          approveMutation.isPending ||
          rejectMutation.isPending
        }
        readOnly={!canWrite}
      />

      {/* Rejection Notification Modal */}
      <RejectionNotificationModal
        isOpen={showRejectionNotification}
        onClose={() => setShowRejectionNotification(false)}
        rejectedCount={rejectedDriftCount}
        isBulkAction={rejectedDriftCount > 1}
      />

      {/* Drift Audit Export Modal */}
      <DriftAuditExportModal
        isOpen={showAuditExportModal}
        onClose={() => setShowAuditExportModal(false)}
        tenantId={filters.tenantId}
        tenantName={userTenants.find(t => t.tenantId === filters.tenantId)?.name}
      />
    </div>
  )
}

/**
 * NoticeRail — a queue notice as Carbon's low-contrast inline notification
 * (info). `eyebrow` is still accepted so callers compile, and is not rendered
 * (the No-Kicker Rule); `tone` no longer changes the color, because a notice
 * is not a state (the Neutral Bad State Rule).
 */
export function NoticeRail({
  eyebrow: _eyebrow,
  tone: _tone = 'default',
  headline,
  body,
  actions,
  className,
}: {
  tone?: 'default' | 'info' | 'archive'
  eyebrow: string
  headline: ReactNode
  body: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <InlineNotification kind="info" title={headline} actions={actions} className={className}>
      {body}
    </InlineNotification>
  )
}
